
import React, { useState } from 'react';
import { Sun, Clock, CalendarDays } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Card } from '@/components/ui/card';
import { INPUT_FIELD_STYLES, FORM_CONTAINER_STYLES, FORM_CONTENT_STYLES, TAB_CONTAINER_STYLES } from './shared-styles';

interface SunnyHours {
  id: string;
  content: {
    from_time: string;
    to_time: string;
    date: string;
    notes?: string;
  };
  submitted_at: string;
}

interface SunnyHoursFormProps {
  existingHours: SunnyHours[];
  onSubmitHours: (fromTime: string, toTime: string, date: string, notes?: string) => Promise<void>;
  loading: boolean;
}

export const SunnyHoursForm: React.FC<SunnyHoursFormProps> = ({
  existingHours,
  onSubmitHours,
  loading
}) => {
  const [fromTime, setFromTime] = useState('');
  const [toTime, setToTime] = useState('');
  const [date, setDate] = useState(new Date().toISOString().split('T')[0]);
  const [notes, setNotes] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!fromTime || !toTime || !date) return;
    
    await onSubmitHours(fromTime, toTime, date, notes.trim() || undefined);
    setFromTime('');
    setToTime('');
    setNotes('');
  };

  return (
    <div className="space-y-6">
      {/* Sunny hours form */}
      <div className="bg-warm-cream/30 rounded-lg p-5 border border-sunset-medium/10">
        <div className="flex items-center gap-3 mb-5">
          <div className="p-2 rounded-lg bg-sunset-yellow/10">
            <Sun className="h-5 w-5 text-sunset-yellow" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-sunset-dark">Report Sunny Hours</h3>
            <p className="text-sm text-sunset-medium">Help others know when this terrace gets sunshine</p>
          </div>
        </div>

        <div className={`${FORM_CONTAINER_STYLES} ${TAB_CONTAINER_STYLES}`}>
          <form onSubmit={handleSubmit} className={FORM_CONTENT_STYLES}>
            <div className="space-y-3">
              <Label htmlFor="date" className="text-base font-semibold text-sunset-dark flex items-center gap-2">
                <CalendarDays className="h-4 w-4" />
                Date
              </Label>
              <Input
                id="date"
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                max={new Date().toISOString().split('T')[0]}
                className={INPUT_FIELD_STYLES}
                required
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-3">
                <Label htmlFor="from-time" className="text-base font-semibold text-sunset-dark">
                  Sunny from
                </Label>
                <Input
                  id="from-time"
                  type="time"
                  step="3600"
                  value={fromTime}
                  onChange={(e) => setFromTime(e.target.value)}
                  className={INPUT_FIELD_STYLES}
                  required
                />
              </div>
              <div className="space-y-3">
                <Label htmlFor="to-time" className="text-base font-semibold text-sunset-dark">
                  Sunny until
                </Label>
                <Input
                  id="to-time"
                  type="time"
                  step="3600"
                  value={toTime}
                  onChange={(e) => setToTime(e.target.value)}
                  className={INPUT_FIELD_STYLES}
                  required
                />
              </div>
            </div>

            <div className="space-y-3">
              <Label htmlFor="notes" className="text-base font-semibold text-sunset-dark">
                Notes <span className="text-sunset-medium font-normal">(optional)</span>
              </Label>
              <Input
                id="notes"
                placeholder="e.g., partial shade after 3pm, very bright morning sun..."
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                className={INPUT_FIELD_STYLES}
              />
            </div>

            <Button 
              type="submit" 
              disabled={!fromTime || !toTime || !date || loading}
              variant="action"
              size="lg"
            >
              <Sun className="h-5 w-5 mr-2" />
              Report Sunny Hours
            </Button>
          </form>
        </div>
      </div>

      {/* Recent sunny hours reports */}
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <h4 className="text-lg font-semibold text-sunset-dark">Recent Reports</h4>
          <Badge variant="secondary" className="bg-sunset-yellow/10 text-sunset-yellow border-sunset-yellow/20">
            {existingHours.length} reports
          </Badge>
        </div>
        
        {existingHours.length > 0 ? (
          <div className="space-y-3">
            {existingHours.slice(0, 5).map((report) => (
              <Card key={report.id} className="p-4 border-sunset-medium/20 hover:shadow-md transition-shadow">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-3">
                    <div className="p-1.5 rounded-md bg-sunset-yellow/10">
                      <Clock className="h-4 w-4 text-sunset-yellow" />
                    </div>
                    <span className="font-semibold text-sunset-dark">
                      {report.content.from_time} - {report.content.to_time}
                    </span>
                  </div>
                  <Badge variant="outline" className="border-sunset-medium/30 text-sunset-medium">
                    {new Date(report.content.date).toLocaleDateString()}
                  </Badge>
                </div>
                {report.content.notes && (
                  <p className="text-sm text-sunset-medium mt-2 ml-8">
                    {report.content.notes}
                  </p>
                )}
              </Card>
            ))}
          </div>
        ) : (
          <div className="text-center py-12 bg-warm-cream/20 rounded-lg border-2 border-dashed border-sunset-medium/30">
            <Sun className="h-16 w-16 mx-auto mb-4 text-sunset-medium/50" />
            <h5 className="text-lg font-medium text-sunset-dark mb-2">No reports yet</h5>
            <p className="text-sunset-medium">Share when you experienced sunshine here!</p>
          </div>
        )}
      </div>
    </div>
  );
};
